import React, { useState, useMemo } from "react";
import { Divider, ListItemIcon, ListItemText } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import {
  selectUserFullName,
  selectUserRoleId,
} from "../../../store/selectors/authSelectors";
import { selectSidebarOpen } from "../../../store/selectors/uiSelectors";
import { logoutAction } from "../../../store/actions/authActions";
import { toggleSidebar, setSidebarOpen } from "../../../store/slices/uiSlice";
import { ROUTES } from "../../../constants";
import { UserRole } from "../../../constants";
import {
  AppLogoIcon,
  AppSmallLogoIcon,
  DashboardIcon,
  PatientsIcon,
  LogOutIcon,
  PersonIcon,
  AppointmentSuccessIcon,
  AddPersonIcon,
  SettingsIcon,
  AuditsIcon,
  SidebarToggleIcon,
} from "../../Icons";
import {
  LogoContainer,
  LogoLink,
  LogoMark,
  LogoVariant,
  LogoutContainer,
  NavList,
  SidebarMenuButton,
  SidebarContainer,
  SidebarContent,
  SidebarItemLabel,
  SIDEBAR_ICON_SIZE,
  sidebarListItemIconSx,
  ToggleButton,
} from "./Sydebar.styled";
import { colors } from "../../../theme/colors";
import { Modal } from "../../Modal";

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const userFullName = useSelector(selectUserFullName);
  const userRoleId = useSelector(selectUserRoleId);
  const isExpanded = useSelector(selectSidebarOpen);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const isManager = userRoleId === UserRole.Manager;

  const menuItems = useMemo(() => {
    const items: Array<{
      icon: React.FC<React.SVGProps<SVGSVGElement>>;
      text: string;
      path: string;
    }> = [
      { icon: DashboardIcon, text: "Dashboard", path: ROUTES.DASHBOARD },
      {
        icon: AppointmentSuccessIcon,
        text: "Sessions",
        path: ROUTES.SESSIONS,
      },
      {
        icon: PatientsIcon,
        text: "Patients",
        path: ROUTES.PATIENTS,
      },
    ];

    if (isManager) {
      items.push(
        {
          icon: AddPersonIcon,
          text: "User Management",
          path: ROUTES.USER_MANAGEMENT,
        },
        {
          icon: SettingsIcon,
          text: "Manage Organization",
          path: ROUTES.MANAGE_ORGANIZATION,
        },
        { icon: AuditsIcon, text: "Audit Logs", path: ROUTES.AUDIT_LOGS },
      );
    }

    return items;
  }, [isManager]);

  const collapseSidebar = () => {
    dispatch(setSidebarOpen(false));
  };

  const handleToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    collapseSidebar();
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await dispatch(logoutAction()).unwrap();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      navigate(ROUTES.LOGIN);
    } finally {
      setShowLogoutModal(false);
      collapseSidebar();
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleProfileClick = () => {
    navigate(ROUTES.PROFILE);
    collapseSidebar();
  };

  const isProfileActive =
    location.pathname === ROUTES.PROFILE ||
    location.pathname === ROUTES.PROFILE_EDIT;

  const selectedItemSx = {
    "&.Mui-selected": {
      backgroundColor: "transparent",
      "& span": {
        color: `${colors.text.action}`,
      },
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
  };

  return (
    <SidebarContainer expanded={isExpanded}>
      <ToggleButton
        expanded={isExpanded}
        onClick={handleToggle}
        aria-label="Expand sidebar"
      >
        <SidebarToggleIcon fill={colors.primary.main} />
      </ToggleButton>

      <SidebarContent>
      <LogoContainer expanded={isExpanded}>
        <LogoLink to={ROUTES.DASHBOARD}>
          <LogoMark expanded={isExpanded}>
            <LogoVariant expanded={isExpanded} variant="full">
              <AppLogoIcon height="40px" width="188px" />
            </LogoVariant>
            <LogoVariant expanded={isExpanded} variant="small">
              <AppSmallLogoIcon height="40px" width="35px" />
            </LogoVariant>
          </LogoMark>
        </LogoLink>
      </LogoContainer>

      <SidebarMenuButton
        expanded={isExpanded}
        onClick={handleProfileClick}
        selected={isProfileActive}
        sx={selectedItemSx}
      >
        <ListItemIcon sx={sidebarListItemIconSx}>
          <PersonIcon
            fill={colors.primary.main}
            width={SIDEBAR_ICON_SIZE}
            height={SIDEBAR_ICON_SIZE}
          />
        </ListItemIcon>
        <SidebarItemLabel expanded={isExpanded}>
          <ListItemText
            primary={userFullName || "User"}
            primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
          />
        </SidebarItemLabel>
      </SidebarMenuButton>

      <Divider sx={{ margin: "32px 0" }} />

      <NavList>
        {menuItems.map((item, index) => (
          <SidebarMenuButton
            key={index}
            expanded={isExpanded}
            onClick={() => handleNavigation(item.path)}
            selected={location.pathname === item.path}
            sx={selectedItemSx}
          >
            <ListItemIcon sx={sidebarListItemIconSx}>
              <item.icon
                fill={colors.primary.main}
                width={SIDEBAR_ICON_SIZE}
                height={SIDEBAR_ICON_SIZE}
              />
            </ListItemIcon>
            <SidebarItemLabel expanded={isExpanded}>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ noWrap: true }}
              />
            </SidebarItemLabel>
          </SidebarMenuButton>
        ))}
      </NavList>

      <LogoutContainer>
        <SidebarMenuButton
          expanded={isExpanded}
          sx={selectedItemSx}
          onClick={handleLogoutClick}
        >
          <ListItemIcon
            sx={{
              ...sidebarListItemIconSx,
              background: colors.background.red,
            }}
          >
            <LogOutIcon width={SIDEBAR_ICON_SIZE} height={SIDEBAR_ICON_SIZE} />
          </ListItemIcon>
          <SidebarItemLabel expanded={isExpanded}>
            <ListItemText
              primary={t("logoutConfirmation.confirm")}
              primaryTypographyProps={{ noWrap: true }}
            />
          </SidebarItemLabel>
        </SidebarMenuButton>
      </LogoutContainer>
      </SidebarContent>

      <Modal
        open={showLogoutModal}
        onClose={handleLogoutCancel}
        title={t("logoutConfirmation.title")}
        primaryButtonText={t("logoutConfirmation.confirm")}
        secondaryButtonText={t("logoutConfirmation.cancel")}
        onPrimaryClick={handleLogoutConfirm}
        onSecondaryClick={handleLogoutCancel}
      >
        <div />
      </Modal>
    </SidebarContainer>
  );
};

export { SidebarBackdrop } from "./Sydebar.styled";
export default Sidebar;
